import React, { useEffect, useMemo, useState } from 'react';
import { PageSubheader, TicketDetailModal, CreateTicketModal } from '../shared';
import { ReturnsDisputesTable, ProcessReturnPanel } from './components';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { ReturnDisputeRow, ProcessReturnDetail } from './callCenterReturnsDisputesTypes';
import { useTicketsStore } from '../../../store/ticketsStore';
import { uiPriority, uiStatus } from '../shared/ticketAdapter';
import { Ticket } from '../../../services/ticketsApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CallCenterReturnsDisputes.module.css';

function adapt(t: Ticket): ReturnDisputeRow {
  return {
    id: String(t.id),
    ticketId: `#${t.ticket_number}`,
    orderId: t.order_id ? formatOrderId(t.order_id) : '—',
    customer: t.customer_name || '—',
    returnReason: t.subject,
    priority: uiPriority(t.priority),
    approvalStatus: uiStatus(t.status),
  };
}

function buildDetail(t: Ticket): ProcessReturnDetail {
  return {
    ticketId: `#${t.ticket_number}`,
    order: t.order_id ? formatOrderId(t.order_id) : '—',
    customer: t.customer_name || '—',
    returnReason: t.subject,
    item: '—',
    condition: t.description || '—',
    priority: uiPriority(t.priority),
  };
}

const CallCenterReturnsDisputes: React.FC = () => {
  const items = useTicketsStore((s) => s.items);
  const loading = useTicketsStore((s) => s.loading);
  const error = useTicketsStore((s) => s.error);
  const saving = useTicketsStore((s) => s.saving);
  const setFilters = useTicketsStore((s) => s.setFilters);
  const fetchList = useTicketsStore((s) => s.fetchList);
  const update = useTicketsStore((s) => s.update);
  const reply = useTicketsStore((s) => s.reply);

  useEffect(() => {
    setFilters({ category: 'returns' });
    fetchList();
  }, [setFilters, fetchList]);

  const rows = useMemo<ReturnDisputeRow[]>(() => items.map(adapt), [items]);
  const detail = useMemo(() => (items[0] ? buildDetail(items[0]) : null), [items]);

  const [openTicketId, setOpenTicketId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className={styles.container}>
      <PageSubheader
        description="Return approval workflow and dispute resolution"
        onAction={() => setShowCreate(true)}
      />
      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && rows.length === 0}
        emptyMessage="No returns or disputes to display"
        onRetry={() => fetchList()}
      >
        <ReturnsDisputesTable rows={rows} onView={(id) => setOpenTicketId(parseInt(id, 10))} />
        {detail && items[0] && (
          <ProcessReturnPanel
            detail={detail}
            busy={saving}
            onApprove={async (notes, condition) => {
              const ticket = items[0];
              const memo = [condition && `Condition: ${condition}`, notes && `Notes: ${notes}`].filter(Boolean).join('\n');
              try {
                if (memo) await reply(ticket.id, `[Return approved]\n${memo}`).catch(() => undefined);
                await update(ticket.id, { status: 'resolved' });
                fetchList();
              } catch (err) {
                window.alert(err instanceof Error ? err.message : 'Failed to approve return');
              }
            }}
            onReject={async (notes, condition) => {
              const ticket = items[0];
              if (!window.confirm('Reject this return? The ticket will be closed.')) return;
              const memo = [condition && `Condition: ${condition}`, notes && `Notes: ${notes}`].filter(Boolean).join('\n');
              try {
                if (memo) await reply(ticket.id, `[Return rejected]\n${memo}`).catch(() => undefined);
                await update(ticket.id, { status: 'closed' });
                fetchList();
              } catch (err) {
                window.alert(err instanceof Error ? err.message : 'Failed to reject return');
              }
            }}
          />
        )}
      </AsyncBoundary>
      <TicketDetailModal ticketId={openTicketId} onClose={() => setOpenTicketId(null)} />
      <CreateTicketModal
        open={showCreate}
        defaultCategory="returns"
        lockCategory
        onClose={() => setShowCreate(false)}
        onCreated={(id) => { setOpenTicketId(id); fetchList(); }}
      />
    </div>
  );
};

export const CallCenterReturnsDisputesPage: React.FC = () => <CallCenterReturnsDisputes />;

export { CallCenterReturnsDisputes };
export default CallCenterReturnsDisputes;
